import { createRoute, Outlet, redirect, useLocation } from "@tanstack/solid-router";
import { rootRoute } from "../__root";
import { api } from "@/lib/api";
import { sessionActor } from "@/lib/session-machine";
import { queryClient } from "@/lib/query-client";
import { appState } from "@fleet/ui";
import { cloneSession, isValidSession, queryKeys } from "@gymlog/shared";
import { RouteTransition } from "@/components/ui/route-transition";
import { readAccountsStateSync } from "@/lib/accounts";
import { resolveActiveAccountView } from "@/lib/active-account";
import { readTabAccountId } from "@/lib/tab-account";
import { SESSION_CHECK_TIMEOUT_MS } from "@/lib/constants";

const fetchSession = async () =>
  api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });
type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

function syncReply(reply: FetchResult): FetchResult {
  const { data: replyData, error: replyError } = reply;
  sessionActor.send({
    type: "SERVER_REPLY",
    session: isValidSession(replyData) ? cloneSession(replyData) : null,
    error: replyError ?? null,
  });
  queryClient.setQueryData(queryKeys.auth.session(), replyData ?? null);
  return reply;
}

function AuthLayout() {
  const location = useLocation();
  appState.markAsReady();
  return (
    <RouteTransition routeKey={location().pathname}>
      <Outlet />
    </RouteTransition>
  );
}

export const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_auth",
  beforeLoad: async ({ search }) => {
    const isAddingAccount = (search as { mode?: string }).mode === "add";
    if (isAddingAccount) {
      return;
    }

    const redirectTarget = (search as { redirect?: string }).redirect;
    const tabAccountId = readTabAccountId();
    if (tabAccountId !== null) {
      const view = resolveActiveAccountView(
        readAccountsStateSync(),
        tabAccountId,
        sessionActor.getSnapshot().context.session,
      );
      if (view.isAuthenticated) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: redirectTarget ?? "/" });
      }
      return;
    }

    const snapshot = sessionActor.getSnapshot();
    if (!snapshot.matches("priming") && (snapshot.context.session || snapshot.context.isVerified)) {
      if (snapshot.context.session) {
        // oxlint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({
          to: (search as { redirect?: string }).redirect ?? "/",
        });
      }
      return;
    }

    const inFlight = fetchSession()
      .then(syncReply)
      .catch(() => null);

    const timedOut = new Promise<null>((resolve) => {
      setTimeout(() => {
        resolve(null);
      }, SESSION_CHECK_TIMEOUT_MS);
    });

    const result = await Promise.race([inFlight, timedOut]);
    if (!result) {
      return;
    }

    // Decide from the session the machine accepted, not from the raw reply: a
    // stale get-session that LOGOUT revoked must not bounce the guest off /login.
    if (sessionActor.getSnapshot().context.session) {
      // oxlint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
    }
  },
  component: AuthLayout,
});
